import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import clsx from 'clsx';
import Backdrop from 'components/Modal/Backdrop';
import {
  filterMonitoringSchedulesBySearch,
} from 'components/MonitoringAgent/utils/monitoringScheduleUtils';
import { buildScheduleRecurrenceSummary } from 'components/MonitoringAgent/utils/scheduleRecurrenceSummary';
import SmallLoader from 'components/SmallLoader/SmallLoader';
import TextField from 'components/TextField/TextField';
import { useGetMonitoringSchedulesQuery } from 'store/slices/valueChainMap/apis/valueChainMapApi';

import type IMonitoringSchedule from 'constants/interfaces/IMonitoringSchedule';
import type IMonitoringSchedulesModal from './interfaces/IMonitoringSchedulesModal';

import styles from './sass/MonitoringSchedulesModal.module.scss';

const formatDateTime = (value?: string): string => {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusClassName = (status?: string): string => {
  switch (status) {
    case 'completed':
      return styles.statusCompleted;
    case 'running':
      return styles.statusRunning;
    case 'failed':
      return styles.statusFailed;
    default:
      return styles.statusSkipped;
  }
};

function MonitoringSchedulesModal({
  open,
  valueChainMapId,
  schedules: fallbackSchedules = [],
  isSchedulesLoading: isFallbackLoading = false,
  isSchedulesError: isFallbackError = false,
  onClose,
  onEditSchedule,
  onOpenRunHistory,
}: IMonitoringSchedulesModal): React.JSX.Element | null {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | undefined>(undefined);

  const hasValidMapId = Number.isInteger(valueChainMapId) && (valueChainMapId ?? 0) > 0;

  const {
    data: schedulesResponse,
    isFetching: isSchedulesFetching,
    isError: isSchedulesQueryError,
  } = useGetMonitoringSchedulesQuery(
    { valueChainMapId: valueChainMapId as number },
    { skip: !open || !hasValidMapId },
  );

  const schedules = schedulesResponse?.data ?? fallbackSchedules;
  const isSchedulesLoading = (open && hasValidMapId && isSchedulesFetching) || isFallbackLoading;
  const isSchedulesError = (open && hasValidMapId && isSchedulesQueryError) || isFallbackError;

  const filteredSchedules = useMemo(
    () => filterMonitoringSchedulesBySearch(schedules, search),
    [schedules, search],
  );

  const selectedSchedule = useMemo(
    () => filteredSchedules.find((schedule) => schedule.id === selectedScheduleId)
      || schedules.find((schedule) => schedule.id === selectedScheduleId),
    [filteredSchedules, schedules, selectedScheduleId],
  );

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    document.body.style.overflow = 'hidden';
    setSearch('');

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!filteredSchedules.length) {
      setSelectedScheduleId(undefined);
      return;
    }

    const stillVisible = filteredSchedules.some((schedule) => schedule.id === selectedScheduleId);

    if (!stillVisible) {
      setSelectedScheduleId(filteredSchedules[0].id);
    }
  }, [filteredSchedules, open, selectedScheduleId]);

  const pausedCount = useMemo(
    () => schedules.filter((schedule) => schedule.paused).length,
    [schedules],
  );

  const handleSelectSchedule = (schedule: IMonitoringSchedule) => {
    setSelectedScheduleId(schedule.id);
  };

  if (!open) {
    return null;
  }

  return createPortal(
    <Backdrop>
      <div
        className={styles.root}
        role="dialog"
        aria-modal="true"
        aria-label={t('valueChainMap.monitoringSchedulesModalTitle')}
      >
        <div className={styles.header}>
          <div className={styles.headerText}>
            <h2 className={styles.title}>{t('valueChainMap.monitoringSchedulesModalTitle')}</h2>
            <div className={styles.kpis}>
              <span className={styles.kpiChip}>
                {t('valueChainMap.monitoringSchedulesModalCount', { count: schedules.length })}
              </span>
              <span className={styles.kpiChip}>
                {t('valueChainMap.monitoringSchedulesModalPausedCount', { count: pausedCount })}
              </span>
            </div>
          </div>
          <button
            type="button"
            className={styles.iconButton}
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <CloseIcon aria-hidden="true" />
          </button>
        </div>

        <div className={styles.toolbar}>
          <p className={styles.hint}>{t('valueChainMap.monitoringSchedulesModalHint')}</p>
          <TextField
            value={search}
            name="monitoringSchedulesModalSearch"
            placeholder={t('valueChainMap.monitoringHubSchedulesSearchPlaceholder')}
            icon={<SearchIcon />}
            className={styles.searchField}
            onChange={({ target }) => setSearch(target.value)}
          />
        </div>

        <div className={styles.layout}>
          <div className={styles.tablePane}>
            {isSchedulesLoading ? (
              <div className={styles.loaderWrapper}>
                <SmallLoader size={30} />
              </div>
            ) : null}

            {!isSchedulesLoading && isSchedulesError ? (
              <p className={styles.stateMessage}>{t('valueChainMap.monitoringSchedulesLoadError')}</p>
            ) : null}

            {!isSchedulesLoading && !isSchedulesError && !filteredSchedules.length ? (
              <p className={styles.stateMessage}>{t('valueChainMap.monitoringSchedulesEmpty')}</p>
            ) : null}

            {!isSchedulesLoading && !isSchedulesError && filteredSchedules.length ? (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('valueChainMap.monitoringSchedulesModalColTitle')}</th>
                    <th>{t('valueChainMap.monitoringSchedulesModalColRecurrence')}</th>
                    <th>{t('valueChainMap.monitoringSchedulesModalColEntities')}</th>
                    <th>{t('valueChainMap.monitoringSchedulesModalColNextRun')}</th>
                    <th>{t('valueChainMap.monitoringSchedulesModalColLastStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSchedules.map((schedule) => (
                    <tr
                      key={schedule.id}
                      className={clsx(styles.scheduleRow, {
                        [styles.scheduleRowSelected]: schedule.id === selectedScheduleId,
                      })}
                      onClick={() => handleSelectSchedule(schedule)}
                    >
                      <td>
                        <div className={styles.scheduleTitleCell}>
                          <span className={styles.scheduleTitle}>{schedule.title}</span>
                          {schedule.paused ? (
                            <span className={styles.pausedBadge}>
                              {t('valueChainMap.monitoringSchedulePausedBadge')}
                            </span>
                          ) : null}
                          <span className={styles.metaMuted}>{schedule.schedule.timezone}</span>
                        </div>
                      </td>
                      <td>
                        <span className={styles.metaMuted}>
                          {buildScheduleRecurrenceSummary(schedule.schedule)}
                        </span>
                      </td>
                      <td>
                        {t('valueChainMap.monitoringScheduleEntityCount', {
                          count: schedule.unitIds.length,
                        })}
                      </td>
                      <td>{formatDateTime(schedule.nextRunAt)}</td>
                      <td>
                        <span className={clsx(styles.statusBadge, getStatusClassName(schedule.lastRunStatus))}>
                          {schedule.lastRunStatus || 'skipped'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </div>

          <div className={styles.detailPane}>
            {!selectedSchedule ? (
              <p className={styles.emptyDetail}>{t('valueChainMap.monitoringSchedulesModalSelectHint')}</p>
            ) : (
              <>
                <div className={styles.detailHeader}>
                  <h3 className={styles.detailTitle}>{selectedSchedule.title}</h3>
                  <span className={styles.metaMuted}>
                    {`${buildScheduleRecurrenceSummary(selectedSchedule.schedule)}`
                      + ` · ${selectedSchedule.schedule.timezone}`}
                  </span>
                </div>

                <div className={styles.detailActions}>
                  {onEditSchedule ? (
                    <button
                      type="button"
                      className={styles.textActionButton}
                      onClick={() => onEditSchedule(selectedSchedule)}
                    >
                      {t('valueChainMap.monitoringScheduleEdit')}
                    </button>
                  ) : null}
                  {onOpenRunHistory ? (
                    <button
                      type="button"
                      className={styles.textActionButton}
                      onClick={() => onOpenRunHistory(selectedSchedule)}
                    >
                      {t('valueChainMap.monitoringRunHistoryTitle')}
                    </button>
                  ) : null}
                </div>

                <p className={styles.hint}>
                  {t('valueChainMap.monitoringSchedulesModalHistoryHint')}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </Backdrop>,
    document.body,
  );
}

export default MonitoringSchedulesModal;
